import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

/**
 * Firebase Auth 에러 메시지를 한국어로 변환
 * @param {string} errorCode - Firebase 에러 코드
 * @param {boolean} isSignup - 회원가입 여부
 * @returns {string} - 한국어 에러 메시지
 */
export const getAuthErrorMessage = (errorCode, isSignup = false) => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return "이미 사용 중인 이메일 주소입니다.";
    case 'auth/invalid-email':
      return "유효하지 않은 이메일 주소입니다.";
    case 'auth/operation-not-allowed':
      return "이메일/비밀번호 로그인이 비활성화되어 있습니다.";
    case 'auth/weak-password':
      return "비밀번호가 너무 약합니다.";
    case 'auth/user-disabled':
      return "계정이 비활성화되었습니다.";
    case 'auth/user-not-found':
      return "등록되지 않은 이메일 주소입니다.";
    case 'auth/wrong-password':
      return "잘못된 비밀번호입니다.";
    case 'auth/network-request-failed':
      return "네트워크 연결에 실패했습니다. 인터넷 연결을 확인해주세요.";
    case 'auth/too-many-requests':
      return "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요.";
    case 'auth/invalid-credential':
      return "잘못된 인증 정보입니다.";
    default:
      // undefined나 알 수 없는 에러 코드 처리
      console.error('알 수 없는 Firebase 에러 코드:', errorCode);
      return isSignup ? "회원가입 중 오류가 발생했습니다." : "로그인 중 오류가 발생했습니다.";
  }
};

/**
 * 비밀번호 재설정 에러 메시지를 한국어로 변환
 * @param {string} errorCode - Firebase 에러 코드
 * @returns {string} - 한국어 에러 메시지
 */
export const getPasswordResetErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return '유효하지 않은 이메일 주소입니다.';
    case 'auth/user-not-found':
      return '등록되지 않은 이메일 주소입니다.';
    default:
      return errorCode;
  }
};

/**
 * 사용자 정보를 Firestore에 저장하는 함수
 * @param {Object} user - Firebase 사용자 객체
 * @param {Object} additionalInfo - 추가 정보 객체
 * @returns {Promise<void>}
 */
export const saveUserToFirestore = async (user, additionalInfo = {}) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    
    // 기존 사용자 정보 확인
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // 새 사용자인 경우에만 저장
      const userData = {
        uid: user.uid,
        email: additionalInfo.email || user.email, // additionalInfo의 email을 우선 사용
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        emailVerified: user.emailVerified,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        loginMethod: additionalInfo.loginMethod || 'email',

        ...additionalInfo
      };
      
      await setDoc(userRef, userData);
      console.log('새 사용자 정보 저장 완료:', userData);
    } else {
      // 기존 사용자인 경우 마지막 로그인 시간과 이메일 정보 업데이트
      const updateData = {
        lastLoginAt: serverTimestamp(),
        emailVerified: user.emailVerified
      };
      
      // additionalInfo에 email이 있는 경우 이메일도 업데이트
      if (additionalInfo.email) {
        updateData.email = additionalInfo.email;
      }
      
      await setDoc(userRef, updateData, { merge: true });
      console.log('기존 사용자 로그인 시간 및 정보 업데이트 완료');
    }
  } catch (error) {
    console.error('사용자 정보 저장 실패:', error);
    throw error; // 에러를 상위로 전파
  }
};

/**
 * Firestore에서 기존 사용자 이메일을 가져오는 함수
 * @param {string} userId - 사용자 ID
 * @returns {Promise<string|null>} - 사용자 이메일 또는 null
 */
export const getUserEmailFromFirestore = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const userData = userSnap.data();
      return userData.email || null;
    }
    return null;
  } catch (error) {
    console.error('Firestore에서 사용자 이메일 가져오기 실패:', error);
    return null;
  }
};

/**
 * 소셜 로그인에서 최종 사용할 이메일을 결정하는 함수
 * @param {string} socialEmail - 소셜 플랫폼에서 받은 이메일
 * @param {string} userId - 사용자 ID
 * @param {string} provider - 소셜 플랫폼 ('kakao', 'naver' 등)
 * @returns {Promise<string>} - 최종 결정된 이메일
 */
export const resolveFinalEmail = async (socialEmail, userId, provider) => {
  // 이메일이 없거나 기본 이메일인 경우 Firestore에서 기존 이메일 확인
  const defaultEmails = [`@${provider}.com`];
  const isDefaultEmail = !socialEmail || defaultEmails.some(domain => socialEmail.includes(domain));
  
  if (isDefaultEmail) {
    const storedEmail = await getUserEmailFromFirestore(userId);
    // 저장된 이메일이 있고, 기본 이메일이 아닌 경우 사용
    if (storedEmail && !defaultEmails.some(domain => storedEmail.includes(domain))) {
      console.log(`Firestore에서 기존 이메일 사용: ${storedEmail}`);
      return storedEmail;
    }
  }
  
  return socialEmail || '';
}; 